/**
 * ContextualSuggestions — Mid-conversation action capsules that appear
 * above the chat input based on workspace context.
 *
 * Unlike STARTER_SUGGESTIONS (shown only on fresh sessions), these
 * appear/disappear dynamically as the session progresses.
 */
import React, { useMemo } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import {
  AutoFixHigh as MapIcon,
  Visibility as ReviewIcon,
  WarningAmber as FixIcon,
  Chat as ExplainIcon,
  Schema as SchemaIcon,
} from '@mui/icons-material';

// ── Types ────────────────────────────────────────────────────────────

export interface Suggestion {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  /** If set, sends this as a chat message. */
  prompt?: string;
  /** If set, calls this callback instead of sending a message. */
  action?: () => void;
}

export interface ContextualSuggestionsProps {
  suggestions: Suggestion[];
  onSendMessage: (msg: string) => void;
}

// ── Suggestion computation helper ────────────────────────────────────

interface SuggestionContext {
  /** Number of source tables loaded. */
  sourceCount: number;
  /** Whether any transforms exist. */
  hasTransforms: boolean;
  /** Batch mapping status. */
  batchStatus?: string;
  /** Number of mappings. */
  mappingCount?: number;
  /** Number of low-confidence mappings. */
  lowConfidenceCount?: number;
  /** Whether we're in mapping mode. */
  inMappingMode: boolean;
  /** Whether a field is selected in mapping canvas. */
  selectedFieldLabel?: string;
  /** Whether mapping generation is in progress. */
  mappingInProgress: boolean;
  /** Whether mapping_complete is set in session meta (mapping has been triggered). */
  mappingComplete?: boolean;
  /** Callbacks. */
  onSwitchToMappingMode?: () => void;
  onOpenMappingReview?: () => void;
  /** Opens the Generate Mapping dialog (schema selector + mode picker). */
  onOpenGenerateMapping?: () => void;
}

export function computeSuggestions(ctx: SuggestionContext): Suggestion[] {
  const suggestions: Suggestion[] = [];

  // Don't show any while mapping is being generated
  if (ctx.mappingInProgress) return suggestions;

  // In mapping mode with field selected → explain
  if (ctx.inMappingMode && ctx.selectedFieldLabel) {
    suggestions.push({
      id: 'explain-field',
      label: 'Explain This Mapping',
      icon: <ExplainIcon sx={{ fontSize: 16 }} />,
      color: '#6366f1',
      prompt: `Explain the mapping for "${ctx.selectedFieldLabel}" — why was this source mapped to this target, and what does the transformation do?`,
    });
    return suggestions; // Only show field-level in mapping mode
  }

  const isMapped = ctx.batchStatus === 'mapped' || ctx.batchStatus === 'approved';
  const isReady = ctx.batchStatus === 'ready_for_mapping';

  // Sources loaded, no mapping yet → open generate mapping dialog
  if (ctx.sourceCount > 0 && !isMapped && isReady && !ctx.hasTransforms && !ctx.mappingComplete) {
    suggestions.push({
      id: 'map-schema',
      label: 'Map to Target Schema',
      icon: <SchemaIcon sx={{ fontSize: 16 }} />,
      color: '#059669',
      action: ctx.onOpenGenerateMapping,
    });
  }

  // Transforms done, no mapping → open generate mapping dialog
  if (ctx.hasTransforms && !isMapped && isReady && !ctx.mappingComplete) {
    suggestions.push({
      id: 'generate-mapping',
      label: 'Generate Mapping',
      icon: <MapIcon sx={{ fontSize: 16 }} />,
      color: '#7c3aed',
      action: ctx.onOpenGenerateMapping,
    });
  }

  // Mapping complete but not reviewed → review
  if (isMapped && ctx.onOpenMappingReview) {
    suggestions.push({
      id: 'review-mapping',
      label: 'Review Mapping',
      icon: <ReviewIcon sx={{ fontSize: 16 }} />,
      color: '#059669',
      action: ctx.onOpenMappingReview,
    });
  }

  // Has low-confidence fields
  if ((ctx.lowConfidenceCount ?? 0) > 0) {
    suggestions.push({
      id: 'fix-low-conf',
      label: `Fix ${ctx.lowConfidenceCount} Low Confidence`,
      icon: <FixIcon sx={{ fontSize: 16 }} />,
      color: '#d97706',
      prompt: `Review and fix the ${ctx.lowConfidenceCount} low-confidence field mappings. Suggest better transformations or source columns.`,
    });
  }

  return suggestions;
}

// ── Component ────────────────────────────────────────────────────────

const ContextualSuggestions: React.FC<ContextualSuggestionsProps> = ({
  suggestions,
  onSendMessage,
}) => {
  if (suggestions.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        px: 1.5,
        py: 1,
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 2 },
      }}
    >
      {suggestions.map((s) => (
        <Box
          key={s.id}
          onClick={() => {
            if (s.action) {
              s.action();
            } else if (s.prompt) {
              onSendMessage(s.prompt);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'all 0.15s',
            '&:hover': {
              borderColor: s.color,
              bgcolor: alpha(s.color, 0.06),
              boxShadow: `0 0 0 1px ${alpha(s.color, 0.15)}`,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 0.75,
              bgcolor: alpha(s.color, 0.1),
              color: s.color,
            }}
          >
            {s.icon}
          </Box>
          <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500 }}>
            {s.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default ContextualSuggestions;
